import { z } from 'zod';
/**
 * Схемы контура auth.
 *
 * Правила заданы один раз и используются обеими сторонами: формой в dashboard
 * и сервером. Тексты ошибок - на русском и сразу пригодны для показа
 * пользователю: сообщение об ошибке, которое нужно переводить в компоненте,
 * рано или поздно разъедется с правилом.
 */
/** Минимальная длина пароля. Совпадает с minPasswordLength в конфигурации Better Auth. */
export const PASSWORD_MIN_LENGTH = 8;
export const emailSchema = z
    .string()
    .trim()
    .min(1, 'Укажите почту')
    .email('Проверьте адрес: похоже, в нём опечатка')
    .max(320, 'Адрес слишком длинный');
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Не короче ${PASSWORD_MIN_LENGTH} символов`)
    .max(128, 'Пароль слишком длинный');
export const displayNameSchema = z
    .string()
    .trim()
    .min(1, 'Как к вам обращаться?')
    .max(64, 'Имя слишком длинное');
/**
 * Часовой пояс - строка IANA.
 *
 * Проверяется не списком, а работоспособностью: Intl знает актуальный набор
 * зон, а зашитый список устаревает при каждом изменении часовых поясов в мире.
 */
export const timezoneSchema = z
    .string()
    .trim()
    .min(1, 'Укажите часовой пояс')
    .refine((value) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}, 'Неизвестный часовой пояс');
export const signUpSchema = z.object({
    name: displayNameSchema,
    email: emailSchema,
    password: passwordSchema,
    timezone: timezoneSchema,
    /**
     * Согласие с условиями. Булево поле, которое обязано быть true: в системе
     * нет чекбокса, роль boolean играет Toggle (docs/DESIGN_SYSTEM.md, 8.4 п. 18).
     */
    acceptTerms: z.literal(true, { message: 'Без согласия с условиями регистрация невозможна' }),
    /** Токен Cloudflare Turnstile. Пусто допустимо: в разработке ключа нет. */
    turnstileToken: z.string().optional(),
});
export const signInSchema = z.object({
    email: emailSchema,
    // На входе длина не проверяется: правило могло измениться с момента
    // регистрации, и отказывать по длине здесь означало бы не пускать
    // пользователя с его же старым паролем.
    password: z.string().min(1, 'Введите пароль'),
    rememberMe: z.boolean(),
});
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z
    .object({
    password: passwordSchema,
    passwordRepeat: z.string(),
})
    .refine((data) => data.password === data.passwordRepeat, {
    message: 'Пароли не совпадают',
    path: ['passwordRepeat'],
});
/**
 * Ответ эндпоинта overlay-токена.
 *
 * Токен отдаётся целиком - его показывает сам пользователь по кнопке. Маскировка
 * живёт в интерфейсе, а не в контракте: сервер не знает, идёт ли сейчас эфир с
 * захватом экрана.
 */
export const overlayTokenSchema = z.object({
    token: z.string(),
    url: z.string(),
    issuedAt: z.string(),
    /**
     * Когда оверлей подключился впервые. `null` - ни разу.
     *
     * Нужен первому пути: пока здесь `null`, ссылка показана строкой в левой
     * колонке, а не спрятана за кнопкой в шапке (решение владельца
     * 2026-09-26, п. 4). Предпросмотр консоли этот признак не выставляет.
     */
    firstSeenAt: z.string().nullable(),
});
/** Разбор результата zod в плоскую карту «поле -> первая ошибка». */
export function fieldErrors(error) {
    const result = {};
    for (const issue of error.issues) {
        const key = issue.path.join('.') || '_form';
        result[key] ??= issue.message;
    }
    return result;
}
//# sourceMappingURL=auth.js.map